import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Paper, { type PaperProps } from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import { enqueueSnackbar } from 'notistack';
import { CUSTOM_BOOKING_STATUS_GROUPS, getCustomBookingStatusGroup } from '@a1/shared';

import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import BookingStatusesDialog from './BookingStatusesDialog';
import type { BookingCustomStatus, BookingCustomStatusSetup } from './types';

export interface SystemStatusOption {
  value: string;
  label: string;
  color: string;
}

export interface SavedCustomStatus {
  id: number;
  name: string;
  color: string;
}

interface StatusChoice {
  choiceKey: string;
  statusGroup: string;
  groupStatusOption: SystemStatusOption;
  customStatus: SavedCustomStatus | null;
  label: string;
  color: string;
}

interface BookingStatusPickerProps {
  bookingId: number;
  savedStatus: string | null;
  savedCustomStatus: SavedCustomStatus | null;
  statusOptions: SystemStatusOption[];
  pendingStatus: SystemStatusOption | null;
  pendingCustomStatusId: number | null | undefined;
  canChangeStatus: boolean;
  canSetCustomStatus: boolean;
  onPendingStatusChange: (statusOption: SystemStatusOption, customStatusId: number | null | undefined) => void;
  onCustomStatusSaved: (customStatus: SavedCustomStatus | null) => void;
}

const BookingStatusPicker = ({
  bookingId,
  savedStatus,
  savedCustomStatus,
  statusOptions,
  pendingStatus,
  pendingCustomStatusId,
  canChangeStatus,
  canSetCustomStatus,
  onPendingStatusChange,
  onCustomStatusSaved
}: BookingStatusPickerProps) => {
  const [bookingCustomStatuses, setBookingCustomStatuses] = useState<BookingCustomStatus[]>([]);
  const [hasLoadedCustomStatuses, setHasLoadedCustomStatuses] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isStatusesDialogOpen, setIsStatusesDialogOpen] = useState(false);

  const showEditStatusesLink = canSetCustomStatus && canChangeStatus;

  const StatusChoicesPaper = useMemo(() => {
    const PaperWithEditLink = forwardRef<HTMLDivElement, PaperProps>(({ children, ...paperProps }, paperRef) => (
      <Paper ref={paperRef} {...paperProps}>
        {children}
        {showEditStatusesLink && (
          <>
            <Divider />
            <Link
              component="button"
              type="button"
              variant="body2"
              fontWeight={600}
              underline="none"
              onMouseDown={(mouseDownEvent) => mouseDownEvent.preventDefault()}
              onClick={() => setIsStatusesDialogOpen(true)}
              sx={{ display: 'block', width: '100%', textAlign: 'left', px: 2, py: 1.25 }}
            >
              Edit statuses and colours
            </Link>
          </>
        )}
      </Paper>
    ));
    PaperWithEditLink.displayName = 'StatusChoicesPaper';
    return PaperWithEditLink;
  }, [showEditStatusesLink]);

  const loadBookingCustomStatuses = useCallback(async () => {
    try {
      const response = await callAzureFunction<BookingCustomStatusSetup>({ url: 'booking-custom-statuses', method: 'get' });
      setBookingCustomStatuses(response.data.customStatuses);
      setHasLoadedCustomStatuses(true);
    } catch (error) {
      enqueueSnackbar({ variant: 'error', message: handleError(error), anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    }
  }, []);

  useEffect(() => {
    if (canSetCustomStatus) {
      loadBookingCustomStatuses();
    }
  }, [canSetCustomStatus, loadBookingCustomStatuses]);

  const savedStatusOption = statusOptions.find((statusOption) => statusOption.value === savedStatus) ?? null;
  const savedStatusGroup = getCustomBookingStatusGroup(savedStatus);
  const shownStatusOption = pendingStatus ?? savedStatusOption;
  const shownStatusGroup = getCustomBookingStatusGroup(shownStatusOption?.value);
  const hasPendingStatusChange = Boolean(pendingStatus && pendingStatus.value !== savedStatus);

  const shownCustomStatus = useMemo(() => {
    if (pendingCustomStatusId !== undefined) {
      return bookingCustomStatuses.find((bookingCustomStatus) => bookingCustomStatus.id === pendingCustomStatusId) ?? null;
    }
    if (hasPendingStatusChange || !savedCustomStatus) {
      return null;
    }
    if (!hasLoadedCustomStatuses) {
      return savedCustomStatus;
    }
    return bookingCustomStatuses.find((bookingCustomStatus) => bookingCustomStatus.id === savedCustomStatus.id) ?? null;
  }, [bookingCustomStatuses, pendingCustomStatusId, hasPendingStatusChange, savedCustomStatus, hasLoadedCustomStatuses]);

  const statusChoices = useMemo(
    () =>
      CUSTOM_BOOKING_STATUS_GROUPS.flatMap((statusGroup) => {
        const groupStatusOption = statusOptions.find((statusOption) => statusOption.value === statusGroup);
        if (!groupStatusOption) {
          return [];
        }
        const plainChoice: StatusChoice = {
          choiceKey: `${statusGroup}-plain`,
          statusGroup,
          groupStatusOption,
          customStatus: null,
          label: groupStatusOption.label,
          color: groupStatusOption.color
        };
        const customChoices: StatusChoice[] = bookingCustomStatuses
          .filter((bookingCustomStatus) => bookingCustomStatus.systemStatus === statusGroup)
          .map((bookingCustomStatus) => ({
            choiceKey: `custom-${bookingCustomStatus.id}`,
            statusGroup,
            groupStatusOption,
            customStatus: { id: bookingCustomStatus.id, name: bookingCustomStatus.name, color: bookingCustomStatus.color },
            label: bookingCustomStatus.name,
            color: bookingCustomStatus.color
          }));
        return [plainChoice, ...customChoices];
      }),
    [statusOptions, bookingCustomStatuses]
  );

  const shownChoice = useMemo(() => {
    if (shownCustomStatus) {
      return (
        statusChoices.find((statusChoice) => statusChoice.customStatus?.id === shownCustomStatus.id) ?? {
          choiceKey: `custom-${shownCustomStatus.id}`,
          statusGroup: shownStatusGroup ?? '',
          groupStatusOption: shownStatusOption as SystemStatusOption,
          customStatus: shownCustomStatus,
          label: shownCustomStatus.name,
          color: shownCustomStatus.color
        }
      );
    }
    return statusChoices.find((statusChoice) => !statusChoice.customStatus && statusChoice.statusGroup === shownStatusGroup) ?? null;
  }, [statusChoices, shownCustomStatus, shownStatusGroup, shownStatusOption]);

  const saveCustomStatusNow = async (customStatus: SavedCustomStatus | null) => {
    if ((customStatus?.id ?? null) === (savedCustomStatus?.id ?? null)) {
      return;
    }
    setIsSaving(true);
    try {
      await callAzureFunction({
        url: `bookings/${bookingId}/custom-status`,
        method: 'patch',
        data: { customStatusId: customStatus?.id ?? null }
      });
      onCustomStatusSaved(customStatus);
    } catch (error) {
      enqueueSnackbar({ variant: 'error', message: handleError(error), anchorOrigin: { vertical: 'top', horizontal: 'right' } });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChoice = async (statusChoice: StatusChoice) => {
    const targetStatusOption =
      savedStatusGroup === statusChoice.statusGroup && savedStatusOption ? savedStatusOption : statusChoice.groupStatusOption;

    if (targetStatusOption.value !== savedStatus) {
      onPendingStatusChange(targetStatusOption, canSetCustomStatus ? (statusChoice.customStatus?.id ?? null) : undefined);
      return;
    }

    onPendingStatusChange(targetStatusOption, undefined);
    if (canSetCustomStatus) {
      await saveCustomStatusNow(statusChoice.customStatus);
    }
  };

  return (
    <Stack spacing={0.5} width="100%">
      {canChangeStatus ? (
        <Stack direction="row" alignItems="center">
          <Typography>Status: </Typography>
          <Autocomplete
            id="select-new-status"
            options={statusChoices}
            groupBy={(statusChoice) => statusChoice.groupStatusOption.label}
            isOptionEqualToValue={(option, selectedChoice) => option.choiceKey === selectedChoice.choiceKey}
            getOptionLabel={(statusChoice) => statusChoice.label}
            value={shownChoice}
            onChange={(_, statusChoice) => statusChoice && handleChoice(statusChoice)}
            disableClearable={Boolean(shownChoice)}
            disabled={isSaving}
            slots={{ paper: StatusChoicesPaper }}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: shownChoice ? <FiberManualRecord style={{ color: shownChoice.color, fontSize: 18 }} /> : null
                  }
                }}
              />
            )}
            renderOption={(props, statusChoice) => (
              <li {...props} key={statusChoice.choiceKey}>
                <FiberManualRecord style={{ color: statusChoice.color, fontSize: 18, marginRight: '10px' }} />
                {statusChoice.customStatus ? statusChoice.label : `${statusChoice.label}`}
              </li>
            )}
            style={{ marginLeft: '5px', minWidth: '200px' }}
          />
        </Stack>
      ) : (
        <Typography>Status: {shownChoice ? <span style={{ color: shownChoice.color }}>{shownChoice.label}</span> : '-'}</Typography>
      )}

      <Typography variant="caption" color="text.secondary">
        System status: <strong>{shownStatusOption?.label ?? '-'}</strong>
        {hasPendingStatusChange && ' (unsaved)'}
      </Typography>

      {isStatusesDialogOpen && (
        <BookingStatusesDialog
          open={isStatusesDialogOpen}
          onClose={() => setIsStatusesDialogOpen(false)}
          onSaved={loadBookingCustomStatuses}
        />
      )}
    </Stack>
  );
};

export default BookingStatusPicker;
